import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Link,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useApolloClient, useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import {
  GET_JOB_APPLICATIONS_BY_JOB,
  GET_JOB_POSTINGS_BY_BUSINESS,
} from "../../../../platform/graphql/queries/careJobs";
import { useSelectedBusiness } from "../../../../platform/redux/store";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import JobApplicationsTable from "../components/jobApplicationsTable";
import { JobPostDialog } from "../../../referralCareDirectory/careReferral/pages/jobPostingDialog";
import UpgradePlan from "../../../../platform/util/upgrade/upgradePlan";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { getEffectivePlan } from "../../../../platform/util/utils";

function formatCompensation(j: any): string | null {
  const { payMin, payMax, payUnit } = j || {};
  if (payMin == null && payMax == null) return null;
  const unitShort = ({ HOUR: "hr", DAY: "day", MONTH: "mo", YEAR: "yr" } as any)[payUnit] || "hr";
  const fmt = (n: number) => `$${Number(n).toLocaleString()}`;
  if (payMin != null && payMax != null) return `${fmt(payMin)}-${fmt(payMax)}/${unitShort}`;
  if (payMin != null) return `${fmt(payMin)}+/${unitShort}`;
  if (payMax != null) return `Up to ${fmt(payMax)}/${unitShort}`;
  return null;
}

function postedLabel(j: any): string | null {
  const ts = j?.publishedAt || j?.createdAt;
  if (!ts) return null;
  try {
    return `Posted ${new Date(ts).toLocaleDateString()}`;
  } catch {
    return null;
  }
}

function extractResumeUrl(a: any): string {
  const raw = a?.resumeUrl ?? a?.resume?.url ?? a?.resumeLink ?? a?.resume_file_url ?? "";
  return String(raw || "").trim();
}

export default function JobManagePage() {
  const { providerUser } = React.useContext(UserContext);
  const client = useApolloClient();
  const selected = useSelectedBusiness();
  const biz = selected?.businessBasic;
  const standardPlanRequired = 2;
  const hasApplicantsAccess =
    Number(getEffectivePlan(providerUser) || 0) >= standardPlanRequired;

  const businessId = React.useMemo(() => {
    const n = Number(biz?.id ?? (biz as any)?.businessId);
    return Number.isFinite(n) ? n : null;
  }, [biz]);

  const { data, loading, refetch } = useQuery(GET_JOB_POSTINGS_BY_BUSINESS, {
    variables: { businessId: businessId as number, onlyActive: false, limit: 100, offset: 0 },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
    errorPolicy: "ignore",
  });

  const jobs = React.useMemo(() => data?.jobPostingsByBusiness ?? [], [data]);
  const [allApplicants, setAllApplicants] = React.useState<any[]>([]);
  const [searchSeekers, setSearchSeekers] = React.useState("");
  const [resumeOnly, setResumeOnly] = React.useState(false);
  const [recentWindow, setRecentWindow] = React.useState<"ALL" | "30">("ALL");
  const [appsLoading, setAppsLoading] = React.useState(false);
  const [appsPartialError, setAppsPartialError] = React.useState<string | null>(null);
  const [selectedJob, setSelectedJob] = React.useState<any | null>(null);
  const [activeTab, setActiveTab] = React.useState<"manage" | "applicants">("manage");
  const [postOpen, setPostOpen] = React.useState(false);
  const [checkPlanNeeded, setCheckPlanNeeded] = React.useState(false);

  React.useEffect(() => {
    if (!selectedJob && jobs.length > 0) {
      setSelectedJob(jobs[0]);
    }
  }, [jobs, selectedJob]);

  React.useEffect(() => {
    let cancelled = false;
    const loadApplicants = async () => {
      if (!jobs.length) {
        setAllApplicants([]);
        return;
      }
      setAppsLoading(true);
      setAppsPartialError(null);
      try {
        const chunks = await Promise.allSettled(
          jobs.map((j: any) =>
            client.query({
              query: GET_JOB_APPLICATIONS_BY_JOB,
              variables: {
                jobId: String(j.id),
                limit: 200,
                offset: 0,
              },
              fetchPolicy: "network-only",
            })
          )
        );
        if (cancelled) return;
        const fulfilled = chunks.filter((r: any) => r.status === "fulfilled") as PromiseFulfilledResult<any>[];
        const rejectedCount = chunks.length - fulfilled.length;
        if (rejectedCount > 0) {
          setAppsPartialError(`${rejectedCount} job${rejectedCount > 1 ? "s" : ""} could not load applicants.`);
        }
        const flat = fulfilled.flatMap((res: any) =>
          (res?.value?.data?.jobApplicationsByJob || []).map((a: any) => ({
            ...a,
            jobTitle: jobTitleFromJob(a, jobs),
          }))
        );
        setAllApplicants(flat);
      } catch (_err) {
        if (!cancelled) setAllApplicants([]);
      } finally {
        if (!cancelled) setAppsLoading(false);
      }
    };
    loadApplicants();
    return () => {
      cancelled = true;
    };
  }, [client, jobs]);

  const seekers = React.useMemo(() => {
    const byKey = new Map<string, any>();
    for (const a of allApplicants) {
      const key = String(a?.applicantEmail || `${a?.applicantName || ""}__${a?.applicantPhone || ""}`).toLowerCase();
      if (!key) continue;
      const curr = byKey.get(key);
      if (!curr) {
        const normalizedResumeUrl = extractResumeUrl(a);
        byKey.set(key, {
          key,
          name: a?.applicantName || "Unknown",
          email: a?.applicantEmail || "",
          phone: a?.applicantPhone || "",
          resumeUrl: normalizedResumeUrl,
          hasResume: Boolean(normalizedResumeUrl),
          jobs: new Set<string>([a?.job?.title || a?.jobTitle || "Job"]),
          latestAt: a?.updatedAt || a?.createdAt || "",
        });
      } else {
        curr.jobs.add(a?.job?.title || a?.jobTitle || "Job");
        const normalizedResumeUrl = extractResumeUrl(a);
        if (!curr.resumeUrl && normalizedResumeUrl) curr.resumeUrl = normalizedResumeUrl;
        if (!curr.hasResume && normalizedResumeUrl) curr.hasResume = true;
        const prevTs = Number(new Date(curr.latestAt || 0));
        const nextTs = Number(new Date(a?.updatedAt || a?.createdAt || 0));
        if (nextTs > prevTs) curr.latestAt = a?.updatedAt || a?.createdAt || curr.latestAt;
      }
    }
    return Array.from(byKey.values()).map((s) => ({
      ...s,
      jobs: Array.from(s.jobs),
    }));
  }, [allApplicants]);

  const filteredSeekers = React.useMemo(() => {
    const q = searchSeekers.trim().toLowerCase();
    const now = Date.now();
    return seekers.filter((s) => {
      if (resumeOnly && !s.hasResume) return false;
      if (recentWindow === "30") {
        const ts = Number(new Date(s.latestAt || 0));
        if (!ts || now - ts > 30 * 24 * 60 * 60 * 1000) return false;
      }
      if (!q) return true;
      return [s.name, s.email, s.phone, ...(s.jobs || [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [seekers, searchSeekers, resumeOnly, recentWindow]);

  if (!businessId) {
    return (
      <Box sx={{ maxWidth: 1100, mx: "auto", px: 2, py: 4 }}>
        <Alert severity="warning">No business selected.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: 2, py: 3 }}>
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <Button
            size="small"
            variant="outlined"
            component={RouterLink}
            to="/care/jobs"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Go to Job Board
          </Button>
        }
      >
        Landed in the wrong portal? Go to Job Board.
      </Alert>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Find applicants"
          requiredPlanId={standardPlanRequired}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Typography variant="subtitle2" color="text.secondary">
              Business Hiring Portal
            </Typography>
            <RadioGroup
              row
              value={activeTab}
              onChange={(e) => {
                const nextTab = e.target.value as "manage" | "applicants";
                if (nextTab === "applicants" && !hasApplicantsAccess) {
                  setCheckPlanNeeded(true);
                  return;
                }
                setActiveTab(nextTab);
              }}
            >
              <FormControlLabel value="manage" control={<Radio size="small" />} label="Manage Jobs" />
              <FormControlLabel value="applicants" control={<Radio size="small" />} label="Find Applicants" />
            </RadioGroup>
          </Stack>
        </Box>

        {activeTab === "manage" || !hasApplicantsAccess ? (
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1.5 }} spacing={1}>
              <Typography variant="h6" fontWeight={700}>Manage Jobs</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => setPostOpen(true)}>
                  Post a Job
                </Button>
                <Button variant="outlined" onClick={() => refetch()} disabled={loading}>
                  Refresh
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5} lg={4}>
                <Stack spacing={1.25}>
                  {loading && jobs.length === 0 ? (
                    <Card variant="outlined"><CardContent>Loading jobs...</CardContent></Card>
                  ) : jobs.length === 0 ? (
                    <Alert severity="info">No jobs yet. Create one to start collecting applications.</Alert>
                  ) : (
                    jobs.map((j: any) => {
                      const comp = formatCompensation(j);
                      const posted = postedLabel(j);
                      const selectedCard = String(selectedJob?.id) === String(j.id);
                      return (
                        <Card
                          key={j.id}
                          variant="outlined"
                          sx={{
                            borderWidth: selectedCard ? 2 : 1,
                            borderColor: selectedCard ? "primary.main" : "divider",
                            bgcolor: selectedCard ? "rgba(25, 118, 210, 0.08)" : "background.paper",
                            boxShadow: selectedCard ? "0 6px 20px rgba(25, 118, 210, 0.18)" : "none",
                            transition: "all 140ms ease",
                          }}
                        >
                          <CardActionArea onClick={() => setSelectedJob(j)}>
                            <CardContent sx={{ p: 2 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
                                  {j.title}
                                </Typography>
                                {selectedCard && <Chip size="small" color="primary" label="Selected" />}
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {j.employmentType && (
                                  <Chip size="small" icon={<WorkOutlineRoundedIcon />} label={j.employmentType} variant="outlined" />
                                )}
                                {j.location && <Chip size="small" label={j.location} variant="outlined" />}
                                {comp && (
                                  <Chip size="small" icon={<MonetizationOnOutlinedIcon />} label={comp} variant="outlined" />
                                )}
                                {typeof j.applicationsCount === "number" && (
                                  <Chip size="small" color="primary" label={`${j.applicationsCount} apps`} variant="outlined" />
                                )}
                                {posted && <Chip size="small" icon={<EventOutlinedIcon />} label={posted} variant="outlined" />}
                                {!j.isActive && <Chip size="small" color="warning" label="Archived" variant="outlined" />}
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      );
                    })
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={7} lg={8}>
                {!selectedJob ? (
                  <Alert severity="info">Select a job to view applicants.</Alert>
                ) : (
                  <JobApplicationsTable jobId={Number(selectedJob.id)} jobTitle={selectedJob.title} />
                )}
              </Grid>
            </Grid>
          </CardContent>
        ) : (
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
              <Typography variant="h6" fontWeight={700}>
                Find Applicants
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  placeholder="Search name, email, phone, resume, or role"
                  value={searchSeekers}
                  onChange={(e) => setSearchSeekers(e.target.value)}
                  sx={{ minWidth: { xs: "100%", sm: 320 } }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="seeker-recent-label">Applied</InputLabel>
                  <Select
                    labelId="seeker-recent-label"
                    value={recentWindow}
                    label="Applied"
                    onChange={(e) => setRecentWindow(e.target.value as "ALL" | "30")}
                  >
                    <MenuItem value="ALL">All time</MenuItem>
                    <MenuItem value="30">Last 30 days</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  size="small"
                  checked={resumeOnly}
                  onChange={(e) => setResumeOnly(e.target.checked)}
                />
              }
              label="Has resume only"
            />
            {!!appsPartialError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {appsPartialError}
              </Alert>
            )}
            <Stack spacing={1} sx={{ mt: 1.25 }}>
              {appsLoading ? (
                <Typography variant="body2" color="text.secondary">Loading applicants...</Typography>
              ) : filteredSeekers.length === 0 ? (
                <Alert severity="info">No caregivers found yet. Applicants from your posted jobs will appear here.</Alert>
              ) : (
                filteredSeekers.map((s) => (
                  <Card key={s.key} variant="outlined">
                    <CardContent sx={{ p: 1.25 }}>
                      <Stack spacing={0.7}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.6}>
                          <Typography fontWeight={700}>{s.name}</Typography>
                          <Stack direction="row" spacing={0.6} useFlexGap sx={{ flexWrap: "wrap" }}>
                            {s.resumeUrl ? (
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                href={s.resumeUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View Resume
                              </Button>
                            ) : (
                              <Chip size="small" label="No resume yet" variant="outlined" />
                            )}
                            {!!s.email && (
                              <Button size="small" variant="contained" component={Link} href={`mailto:${s.email}`}>
                                Connect
                              </Button>
                            )}
                            {!!s.phone && (
                              <Button size="small" variant="outlined" component={Link} href={`tel:${s.phone}`}>
                                Call
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {s.email || "-"} {s.phone ? `- ${s.phone}` : ""}
                        </Typography>
                        <Stack direction="row" spacing={0.6} useFlexGap sx={{ flexWrap: "wrap" }}>
                          {s.jobs.slice(0, 4).map((j: string) => (
                            <Chip key={`${s.key}-${j}`} size="small" label={j} />
                          ))}
                          {s.jobs.length > 4 && <Chip size="small" label={`+${s.jobs.length - 4} more`} />}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          </CardContent>
        )}
      </Card>

      <JobPostDialog
        open={postOpen}
        onClose={() => setPostOpen(false)}
        businessId={businessId}
        onSaved={async () => {
          await refetch();
          setPostOpen(false);
          setActiveTab("manage");
        }}
      />
    </Box>
  );
}

function jobTitleFromJob(a: any, jobs: any[]) {
  const title = a?.job?.title;
  if (title) return title;
  const candidate = jobs.find((j) => String(j?.id) === String(a?.jobId));
  return candidate?.title || "Job";
}
